import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { INFOLINKS_ENABLED } from "@/const";
import { allowed, readConsent, visitorIsRegulated, OPEN_CONSENT_EVENT } from "@/lib/consent";

/**
 * Infolinks in-text/in-tag ad network. Unlike AdSense's AdBanner, this
 * isn't a per-slot display unit placed at a specific spot on a specific
 * page -- Infolinks works by scanning the whole page's text for
 * keywords and inserting its own ad units automatically, so the
 * integration is a single site-wide script rather than a component
 * rendered per ad placement. Mounted once at the app root (see App.tsx),
 * the same way AuthAnalyticsTracker and ScrollToTop are -- not tied to
 * any specific route.
 *
 * Gated the same way as InlineAd/InArticleAd in AdBanner.tsx: hidden
 * while Pro status is still loading (so it never flashes in and out),
 * and never loaded at all for Pro subscribers, matching the ad-free
 * experience already promised on the pricing cards. Since this is a
 * global script rather than a per-render ad unit, the gating happens by
 * conditionally injecting the script tags at all, not by hiding a
 * rendered element.
 *
 * Publisher ID (infolinks_pid) and website ID (infolinks_wsid) are from
 * the real Infolinks account for this site -- not a placeholder.
 */
export function InfolinksScript() {
  const { isAuthenticated } = useAuth();
  const billingStatus = trpc.billing.status.useQuery(undefined, {
    enabled: isAuthenticated,
  });
  const injected = useRef(false);
  const [consentVersion, setConsentVersion] = useState(0);
  useEffect(() => {
    const h = () => setConsentVersion(v => v + 1);
    window.addEventListener("fdp-consent-saved", h);
    return () => window.removeEventListener("fdp-consent-saved", h);
  }, []);

  const stillLoadingProStatus = isAuthenticated && billingStatus.isLoading;
  const isPro = billingStatus.data?.isPro ?? false;

  useEffect(() => {
    if (!INFOLINKS_ENABLED) return;
    if (injected.current) return;
    if (stillLoadingProStatus) return;
    if (isPro) return;
    // In the EEA, UK and Switzerland this ad network loads only after the visitor opted in to advertising.
    if (!allowed("ads", visitorIsRegulated(), readConsent())) return;

    injected.current = true;

    const configScript = document.createElement("script");
    configScript.type = "text/javascript";
    configScript.text = "var infolinks_pid = 3447104; var infolinks_wsid = 0;";
    document.body.appendChild(configScript);

    const mainScript = document.createElement("script");
    mainScript.type = "text/javascript";
    mainScript.src = "//resources.infolinks.com/js/infolinks_main.js";
    mainScript.async = true;
    document.body.appendChild(mainScript);
  }, [stillLoadingProStatus, isPro, consentVersion]);

  return null;
}
